import { apiClient, type Paginated } from '@/core/api';
import type { School } from '@/stores/session';
import type {
  AcademicYearDto,
  AcademicYearWriteInput,
  CreateSchoolInput,
  MySchoolDto,
  UpdateSchoolInput,
} from './types';

/**
 * `GET /api/schools/mine/` — the caller's active memberships (school + role).
 * A DRF @action returning a bare array (not paginated).
 */
export async function getMySchools(): Promise<MySchoolDto[]> {
  const { data } = await apiClient.get<MySchoolDto[]>('/schools/mine/');
  return data;
}

/**
 * `POST /api/schools/` — self-serve onboarding: creates the school and makes
 * the caller its admin Member. Returns the created School (201).
 */
export async function createSchool(input: CreateSchoolInput): Promise<School> {
  const { data } = await apiClient.post<School>('/schools/', input);
  return data;
}

/**
 * `PATCH /api/schools/{public_id}/` — the school's own settings. Restricted
 * server-side to an admin Member of THAT school (backend ADR-016): any other
 * role gets 403, a stranger 404.
 */
export async function updateSchool(publicId: string, input: UpdateSchoolInput): Promise<School> {
  const { data } = await apiClient.patch<School>(`/schools/${publicId}/`, input);
  return data;
}

// ---------------------------------------------------------------------------
// Academic years (backend ADR-031).
// ---------------------------------------------------------------------------

/** `GET /api/academic-years/` — readable by every member (class payloads
 *  reference years); ordered newest first. */
export async function getAcademicYears(): Promise<Paginated<AcademicYearDto>> {
  const { data } = await apiClient.get<Paginated<AcademicYearDto>>('/academic-years/', {
    params: { page_size: 50 },
  });
  return data;
}

/** `POST /api/academic-years/` — admin only. The new year is NOT current. */
export async function createAcademicYear(
  input: AcademicYearWriteInput
): Promise<AcademicYearDto> {
  const { data } = await apiClient.post<AcademicYearDto>('/academic-years/', input);
  return data;
}

/** `PATCH /api/academic-years/{public_id}/` — rename / adjust dates. */
export async function updateAcademicYear(
  publicId: string,
  input: Partial<AcademicYearWriteInput>
): Promise<AcademicYearDto> {
  const { data } = await apiClient.patch<AcademicYearDto>(`/academic-years/${publicId}/`, input);
  return data;
}

/** `POST /api/academic-years/{public_id}/set-current/` — the atomic swap:
 *  exactly one current year per school. */
export async function setCurrentAcademicYear(publicId: string): Promise<AcademicYearDto> {
  const { data } = await apiClient.post<AcademicYearDto>(
    `/academic-years/${publicId}/set-current/`
  );
  return data;
}

/** `DELETE /api/academic-years/{public_id}/` — refused while current
 *  (`year_is_current`) or holding classes (`year_has_classes`). */
export async function deleteAcademicYear(publicId: string): Promise<void> {
  await apiClient.delete(`/academic-years/${publicId}/`);
}
